import { type FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  type ApiError,
  type CreateEventInput,
  createEvent,
  hasApiKey,
  toApiError,
} from '../../api/client';
import type { CreateEventResponse, Endpoint } from '../../types';

interface SendEventFormProps {
  endpoints: Endpoint[];
  endpointsLoaded: boolean;
  onSent: () => void;
}

export function SendEventForm({ endpoints, endpointsLoaded, onSent }: SendEventFormProps) {
  const [eventType, setEventType] = useState('');
  const [payload, setPayload] = useState('{}');
  const [benchmarkRunId, setBenchmarkRunId] = useState('');
  const [idempotencyKey, setIdempotencyKey] = useState('');
  const [selected, setSelected] = useState<string[]>([]);

  const [submitting, setSubmitting] = useState(false);
  const [payloadError, setPayloadError] = useState<string | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [result, setResult] = useState<CreateEventResponse | null>(null);

  if (!hasApiKey) {
    return (
      <p className="notice notice--muted">
        Sending events needs a write key. Set <code className="mono">VITE_API_KEY</code> in{' '}
        <code className="mono">apps/dashboard/.env.local</code> and restart the dev server. Every
        read-only view works without it.
      </p>
    );
  }

  function toggleEndpoint(id: string): void {
    setSelected((previous) =>
      previous.includes(id) ? previous.filter((value) => value !== id) : [...previous, id],
    );
  }

  async function handleSubmit(submitEvent: FormEvent<HTMLFormElement>): Promise<void> {
    submitEvent.preventDefault();
    setError(null);
    setPayloadError(null);
    setResult(null);

    const raw = payload.trim() === '' ? '{}' : payload;
    let parsedPayload: unknown;
    try {
      parsedPayload = JSON.parse(raw);
    } catch (parseError) {
      setPayloadError(parseError instanceof Error ? parseError.message : 'Payload is not valid JSON.');
      return;
    }

    const input: CreateEventInput = { eventType: eventType.trim(), payload: parsedPayload };
    if (selected.length > 0) input.endpointIds = selected;
    if (benchmarkRunId.trim() !== '') input.benchmarkRunId = benchmarkRunId.trim();

    setSubmitting(true);
    try {
      const response = await createEvent(input, idempotencyKey.trim() || undefined);
      setResult(response);
      onSent();
    } catch (requestError) {
      setError(toApiError(requestError));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form className="form" onSubmit={(submitEvent) => void handleSubmit(submitEvent)}>
      <div className="form__row">
        <div className="field">
          <label className="field__label" htmlFor="send-event-type">
            Event type <span className="field__required">required</span>
          </label>
          <input
            id="send-event-type"
            className="input mono"
            value={eventType}
            onChange={(changeEvent) => setEventType(changeEvent.target.value)}
            placeholder="invoice.paid"
            required
            autoComplete="off"
          />
        </div>

        <div className="field">
          <label className="field__label" htmlFor="send-benchmark">
            Benchmark run ID <span className="field__optional">optional</span>
          </label>
          <input
            id="send-benchmark"
            className="input mono"
            value={benchmarkRunId}
            onChange={(changeEvent) => setBenchmarkRunId(changeEvent.target.value)}
            placeholder="tags this event for the stats filter"
            autoComplete="off"
          />
        </div>
      </div>

      <div className="field">
        <label className="field__label" htmlFor="send-payload">
          Payload <span className="field__optional">JSON</span>
        </label>
        <textarea
          id="send-payload"
          className="input input--textarea mono"
          value={payload}
          onChange={(changeEvent) => setPayload(changeEvent.target.value)}
          rows={6}
          spellCheck={false}
          aria-invalid={payloadError !== null}
          aria-describedby={payloadError ? 'send-payload-error' : undefined}
        />
        {payloadError ? (
          <p id="send-payload-error" className="field__error">
            {payloadError}
          </p>
        ) : null}
      </div>

      <div className="field">
        <span className="field__label">
          Target endpoints <span className="field__optional">optional</span>
        </span>
        {!endpointsLoaded ? (
          <p className="field__help">Loading endpoints…</p>
        ) : endpoints.length === 0 ? (
          <p className="field__help">
            No endpoints registered yet. Register one below first, or the event will be ingested with
            nothing to fan out to.
          </p>
        ) : (
          <>
            <p className="field__help">
              Leave all unchecked to fan out to every endpoint subscribed to this event type.
            </p>
            <ul className="checklist">
              {endpoints.map((endpoint) => (
                <li key={endpoint.id}>
                  <label className="checkbox">
                    <input
                      type="checkbox"
                      checked={selected.includes(endpoint.id)}
                      onChange={() => toggleEndpoint(endpoint.id)}
                    />
                    <span className="mono">{endpoint.url}</span>
                    {endpoint.disabled ? <span className="tag tag--danger">disabled</span> : null}
                  </label>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>

      <div className="field">
        <label className="field__label" htmlFor="send-idempotency">
          Idempotency-Key <span className="field__optional">optional</span>
        </label>
        <input
          id="send-idempotency"
          className="input mono"
          value={idempotencyKey}
          onChange={(changeEvent) => setIdempotencyKey(changeEvent.target.value)}
          placeholder="resend with the same key to prove deduplication"
          autoComplete="off"
        />
      </div>

      <div className="form__actions">
        <button type="submit" className="btn btn--primary" disabled={submitting || eventType.trim() === ''}>
          {submitting ? 'Sending…' : 'Send test event'}
        </button>
      </div>

      {error ? (
        <p className="notice notice--danger" role="alert">
          {error.message}
          {error.detail ? <span className="mono"> — {error.detail}</span> : null}
        </p>
      ) : null}

      {result ? (
        <div className="notice notice--success" role="status">
          <p>
            Accepted as <code className="mono">{result.eventId || 'unknown id'}</code> —{' '}
            {result.deliveries.length} deliver{result.deliveries.length === 1 ? 'y' : 'ies'} queued.
          </p>
          {result.eventId ? (
            <p>
              <Link className="link" to={`/events/${encodeURIComponent(result.eventId)}`}>
                Open the delivery timeline →
              </Link>
            </p>
          ) : null}
        </div>
      ) : null}
    </form>
  );
}
